<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Prep Process Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="prep_process.name" class="mb-3">
      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <button
              v-show="checkPermission('delete prep process')"
              title="Delete prep process"
              class="btn btn-danger ml-1"
              @click="showDeleteModal(prep_process.id)"
            >
              <i class="fal fa-trash" />
            </button>
          </template>
        </header-button-group>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read prep process'">
      <template #gated-content>
        <prep-process-card :prep_process="prep_process" />
        <h2>
          Process Tasks
          <b-button
            v-show="checkPermission('add prep task')"
            variant="success"
            size="sm"
            :href="`#/prep-processes/${prep_process_id}/task/add/`"
            class="float-right ml-1"
            title="Add prep task"
          >
            <i class="fal fa-plus" /> Task
          </b-button>
          <b-button
            v-show="checkPermission('edit prep task')"
            variant="primary"
            size="sm"
            :href="`#/prep-processes/${prep_process_id}/sort/`"
            class="float-right ml-1"
            title="Sort prep tasks"
          >
            <i class="fal fa-sort-numeric-down" /> Sort
          </b-button>
        </h2>
        <permission-alert-gate :permissions="'list prep task'">
          <template #gated-content>
            <prep-task-server-table @deletePrepTask="deletePrepTask" />
          </template>
        </permission-alert-gate>
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import PrepProcessCard from "@/components/lab/prepprocess/PrepProcessCard";
import PrepTaskServerTable from "@/components/lab/preptask/PrepTaskServerTable";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel, getCollection } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { prepProcessNotifications } from "@/shared/notifications";

const PrepProcessRepository = LabRepositoryFactory.get("prepProcess");
const PrepTaskRepository = LabRepositoryFactory.get("prepTask");

Vue.use(VueNotifications, notificationOptions);

export default {
  name: "PrepProcessDetail",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    PermissionAlertGate,
    PrepProcessCard,
    PrepTaskServerTable,
  },
  data() {
    return {
      loading: true,
      prep_process_id: this.$route.params.prep_process_id || null,
      prep_process: null,
      prep_task_collection: null,
      links: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-list",
            linkto: "/prep-processes",
            title: "Prep Process list",
          },
          {
            icon: "fa-pencil",
            linkto: `/prep-processes/${this.prep_process_id}/edit`,
            title: "Edit Prep Process",
            variant: "warning",
            permission: this.checkPermission("edit prep process"),
          },
        ],
      };
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    async init() {
      const filters = [
        {
          logic: "filterEquals",
          field: "prep_process_id",
          value: this.prep_process_id,
        },
      ];

      this.prep_process = await getModel({
        model_type: "prep_process",
        id: this.prep_process_id,
        message: this.$bvToast,
      });
      this.prep_task_collection = await getCollection({
        collection_type: "prep_task",
        filters,
        message: this.$bvToast,
      });

      this.loading = false;
    },
    async deletePrepTask(id, ref) {
      try {
        const { data } = await PrepTaskRepository.suspend(id);

        this.$bvToast.toast("Prep Task deleted.", {
          title: data,
          variant: "success",
        });

        ref.refresh();
      } catch ({ response }) {
        this.$bvToast.toast(response.data.error, {
          title: "Prep Task delete Failed.",
          variant: "danger",
        });
      }
    },
    showDeleteModal(id) {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this prep process. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await PrepProcessRepository.suspend(id);
  
              this.$router.push("/prep-processes");
  
              this.deleteSuccess();
            } catch ({ response }) {
              this.$bvToast.toast(response.data.error, {
                title: "Prep Process delete Failed.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
  notifications: prepProcessNotifications,
};
</script>
